import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import UpdatePaymentMethodModal from '../subscription/UpdatePaymentMethodModal';
import { useTawkTo } from '../../hooks/useTawkTo';
import { tawkToConfig } from '../../config/tawkto';
import { useSubscription } from '../../context/SubscriptionContext';
import './AppMenu.css';

interface AppMenuProps {
  className?: string;
}

const AppMenu: React.FC<AppMenuProps> = ({ className = '' }) => {
  const { logout } = useAuth();
  const { hasActiveSubscription, hasHadTrial } = useSubscription();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [showUpdatePaymentModal, setShowUpdatePaymentModal] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Initialize tawk.to but keep it hidden by default (we'll control it manually)
  const tawkToControls = useTawkTo({
    propertyId: tawkToConfig.propertyId,
    widgetId: tawkToConfig.widgetId,
    enabled: tawkToConfig.features.enabled && tawkToConfig.features.showInApp,
    customAttributes: {
      ...tawkToConfig.defaultAttributes,
      page: 'app',
      userType: hasActiveSubscription ? 'subscriber' : (hasHadTrial ? 'trial_used' : 'trial_eligible'),
    },
    onLoad: () => {
      // Hide the widget by default - we'll show it via menu
      tawkToControls.hide();
      
      // Inject custom CSS for dark theme
      if (tawkToConfig.styling.customCSS) {
        const style = document.createElement('style');
        style.textContent = tawkToConfig.styling.customCSS;
        document.head.appendChild(style);
      }
    },
  });

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      console.error('Logout failed:', error);
    }
  };

  const handleCancelSubscription = () => {
    setIsOpen(false);
    setShowCancelModal(true);
  };

  const handleSupportChat = () => {
    setIsOpen(false);
    // Toggle the tawk.to chat widget
    if (tawkToControls.isLoaded()) {
      tawkToControls.toggle();
    } else {
      console.warn('Tawk.to is not loaded yet');
    }
  };

  return (
    <div className={`relative ${className}`} ref={menuRef}>
      {/* Hamburger button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="p-2 rounded-md hover:bg-gray-700 transition-colors"
        aria-label="Menu"
      >
        <svg
          className="w-6 h-6 text-white"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 6h16M4 12h16M4 18h16"
          />
        </svg>
      </button>

      {/* Dropdown menu */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-gray-800 rounded-md shadow-lg py-1 z-50 app-menu-dropdown">
          {/* Support Chat Option */}
          {tawkToConfig.features.enabled && (
            <>
              <button
                onClick={handleSupportChat}
                className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
              >
                <svg 
                  className="w-4 h-4 mr-2" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" 
                  />
                </svg>
                Support Chat
              </button>
              <div className="border-t border-gray-700 my-1"></div>
            </>
          )}
          
          <button
            onClick={() => {
              setIsOpen(false);
              setShowUpdatePaymentModal(true);
            }}
            className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
          >
            <svg 
              className="w-4 h-4 mr-2" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" 
              />
            </svg>
            Update Payment Method
          </button>
          <div className="border-t border-gray-700 my-1"></div>
          <button
            onClick={handleCancelSubscription}
            className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
          >
            Cancel Subscription
          </button>
          <div className="border-t border-gray-700 my-1"></div>
          <button
            onClick={handleLogout}
            className="block w-full text-left px-4 py-2 text-sm text-gray-200 hover:bg-gray-700 transition-colors"
          >
            Sign Out
          </button>
        </div>
      )}

      {/* Cancel Subscription Modal */}
      {showCancelModal && (
        <CancelSubscriptionModal onClose={() => setShowCancelModal(false)} />
      )}

      {/* Update Payment Method Modal */}
      {showUpdatePaymentModal && (
        <UpdatePaymentMethodModal 
          onClose={() => setShowUpdatePaymentModal(false)}
          onSuccess={() => {
            // Optionally show a success message or refresh data
            console.log('Payment method updated successfully');
          }}
        />
      )}
    </div>
  );
};

export default AppMenu; 